// The sub-action view: the Initiatives tab laid out one row per sub-action,
// under its initiative, with every detail the sub-action pop-up has (status,
// late, who, dates, what it waits on, what it holds up, Merlin's note, cited
// sources, how many quotes). Click a row for the pop-up; the sibling names
// in Waits on / Holds up open that sibling; the initiative heading opens the
// whole initiative. Same table shell as the list view.

import { Fragment, type MouseEvent } from 'react';
import type { InitiativeState } from '../../services/roadmapReports';
import type { ModalSection } from './TimelineChart';
import { ACTION_STATUS_LABELS, STATUS_LABELS, teamBarStyle, type ActionRef, type TeamColors } from './roadmapViewUtils';
import { subActionGroups, subActionTotals, subActionTotalsLine, type SubActionRow } from './subActionsUtils';
import tableStyles from '../RoadmapReportsPage.module.css';
import styles from './RoadmapV2.module.css';

interface Props {
  /** The visible initiatives, in the page's sort order. */
  initiatives: readonly InitiativeState[];
  teamColors: TeamColors;
  /** Open the pop-up: with an action key, scoped to that sub-action. */
  onSelect: (key: string, section?: ModalSection, action?: string) => void;
  /** ISO day; injectable clock for tests. */
  today?: string;
}

const COLUMNS = 7;

function RefButtons({ refs, parentKey, label, onOpen }: { refs: ActionRef[]; parentKey: string; label: string; onOpen: (key: string) => void }) {
  if (refs.length === 0) return <span className={tableStyles.muted}>—</span>;
  return (
    <span className={styles.subActionRefs} role="group" aria-label={label}>
      {refs.map((r) => (
        <button
          key={r.key}
          type="button"
          className={styles.subActionRef}
          onClick={(e: MouseEvent) => { e.stopPropagation(); onOpen(r.key); }}
          title={`Open this sub-action`}
          data-action="roadmap_action_open"
          data-target-initiative-key={parentKey}
          data-target-action-key={r.key}
        >
          {r.title}
        </button>
      ))}
    </span>
  );
}

function Row({ r, onSelect }: { r: SubActionRow; onSelect: Props['onSelect'] }) {
  const i = r.initiative;
  const a = r.action;
  const openSibling = (key: string) => onSelect(i.initiative_key, 'dates', key);
  return (
    <tr
      className={`${styles.subActionRow} ${a.status === 'done' ? styles.subActionDone : ''}`}
      onClick={() => onSelect(i.initiative_key, 'dates', a.key)}
      title={`Open this sub-action of ${i.title}`}
      data-action="roadmap_action_open"
      data-target-initiative-key={i.initiative_key}
      data-target-action-key={a.key}
      data-testid={`subaction-${i.initiative_key}-${a.key}`}
    >
      <td>
        <div className={styles.actionTitle}>
          {/* A real button, so the row is reachable by keyboard; its click bubbles to the row. */}
          <button type="button" className={styles.actionOpen}>{a.title}</button>
        </div>
        {a.note && <div className={styles.actionNote}>{a.note}</div>}
        {a.sources.length > 0 && (
          <div className={styles.actionNote} title="Evidence Merlin cited for this sub-action">{a.sources.join(' · ')}</div>
        )}
      </td>
      <td>
        {a.people.length === 0 ? <span className={tableStyles.muted}>—</span> : a.people.map((p) => <span key={p} className={styles.personTag}>{p}</span>)}
      </td>
      <td>
        <span className={`${styles.actionStatus} ${styles[`action_${a.status}`] ?? ''}`}>{ACTION_STATUS_LABELS[a.status] ?? a.status}</span>
        {r.late && <> <span className={`${styles.actionStatus} ${styles.actionLateChip}`}>Late</span></>}
      </td>
      <td>
        <span className={styles.actionDates} title="Stated start → stated end (completion date once done). A ? is a date nobody stated.">
          <span className={r.start ? undefined : styles.needsTarget}>{r.start ?? '?'}</span>
          <span className={styles.actionDatesArrow} aria-hidden="true">→</span>
          <span className={r.late ? styles.actionLate : r.end ? undefined : styles.needsTarget} title={r.late ? 'Stated end date has passed and the sub-action is not done' : r.endKind === 'completed' ? 'Completed on' : undefined}>
            {r.end ?? '?'}
          </span>
        </span>
      </td>
      <td><RefButtons refs={r.waitsOn} parentKey={i.initiative_key} label="Waits on" onOpen={openSibling} /></td>
      <td><RefButtons refs={r.holdsUp} parentKey={i.initiative_key} label="Holds up" onOpen={openSibling} /></td>
      <td>
        {r.quotes === 0
          ? <span className={tableStyles.muted} title="No quote recorded for this sub-action yet">—</span>
          : <span title="Quotes Merlin attached to this sub-action; the pop-up shows them">{r.quotes} quote{r.quotes === 1 ? '' : 's'}</span>}
      </td>
    </tr>
  );
}

export function SubActionsTable({ initiatives, teamColors, onSelect, today }: Props) {
  const day = today ?? new Date().toISOString().slice(0, 10);
  const groups = subActionGroups(initiatives, day);
  const totals = subActionTotals(groups);

  return (
    <section className={tableStyles.structPanel} aria-label="Roadmap — every sub-action with its details" data-testid="subactions-view">
      <div className={tableStyles.structHead}>
        <h2>Sub-actions — every action with its details</h2>
        <span className={tableStyles.structMeta} data-testid="subactions-totals">
          {groups.length === 0 ? 'no sub-actions' : subActionTotalsLine(totals)} · click a row for its pop-up
        </span>
      </div>
      {groups.length === 0 && (
        <div className={styles.muted}>No sub-actions under these filters. Initiatives without separate actions are on the list view.</div>
      )}
      {groups.length > 0 && (
        <div className={tableStyles.structTableWrap}>
          <table className={tableStyles.structTable}>
            <thead>
              <tr>
                <th>Sub-action</th>
                <th>Who</th>
                <th>Status</th>
                <th>Dates</th>
                <th>Waits on</th>
                <th>Holds up</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const i = g.initiative;
                return (
                  <Fragment key={i.initiative_key}>
                    <tr className={styles.groupHead} data-testid={`subactions-group-${i.initiative_key}`}>
                      <td colSpan={COLUMNS} style={teamBarStyle(i.teams, teamColors)}>
                        <button
                          type="button"
                          className={styles.linkBtn}
                          onClick={() => onSelect(i.initiative_key)}
                          title="Open the whole initiative"
                          data-action="roadmap_initiative_open"
                          data-target-initiative-key={i.initiative_key}
                        >
                          {i.title}
                        </button>
                        <span className={`${tableStyles.statusChip} ${tableStyles[`status_${i.status}`] ?? ''}`} style={{ marginLeft: 8 }}>{STATUS_LABELS[i.status] ?? i.status}</span>
                        <span className={styles.meta}> ({g.rows.length} sub-action{g.rows.length === 1 ? '' : 's'})</span>
                      </td>
                    </tr>
                    {g.rows.map((r) => <Row key={`${i.initiative_key}:${r.action.key}`} r={r} onSelect={onSelect} />)}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
